import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import moment from 'moment';
import { getCurrencySymbol, numberFormatter } from 'utils/currency';
import { CommonDateOnlyFormat } from 'helpers/common.helper';
import usePermissions from 'hooks/usePermissions';
import PermissionAllowed from 'ui-component/permissions/PermissionAllowed';
import { FaPaperclip } from 'react-icons/fa';
import DeleteConfirmation from 'ui-component/confirmation-dialogs/deleteConfirmation/DeleteConfirmation';
import { DeleteIcon } from 'ui-component/custom-icons/icons';
import useIsMobile from 'utils/isMobileUtils';

// ✅ Clear
const CreditPaymentComponent = ({ details, onHandleReloadCreditNote }) => {
    const [allPermissions, hasEveryPermission] = usePermissions();
    const history = useNavigate();
    const [showDeletePayment, setShowDeletePayment] = useState(false);
    const isMobile = useIsMobile();

    const handleOpenNewTab = (pageModule, id) => {
        let url = '';
        if (pageModule == 'products') {
            url = `/products/${id}`;
        } else if (pageModule == 'contacts') {
            url = `/contacts/${id}`;
        } else if (pageModule == 'purchase_orders') {
            url = `/purchasing/view/${id}`;
        } else if (pageModule == 'sales_orders') {
            url = `/sales/sales-orders/${id}`;
        } else if (pageModule == 'invoices') {
            url = `/sales/invoices/${id}`;
        } else if (pageModule == 'quotations') {
            url = `/sales/quotations/${id}`;
        }
        if (url) {
            history(url);
        }
    };

    const deletePayment = (id) => {
        setShowDeletePayment(true);
    };
    const handleClose = () => {
        setShowDeletePayment(false);
    };


    // --- MOBILE VERSION ---
    if (isMobile) {
        return (
            <div className="mb-3 p-3 p-xl-4 w-100 new-card">
                <h2 className="font600" style={{ fontSize: '18px', borderBottom: '1px solid #e0e0e0', paddingBottom: '10px', marginBottom: 0 }}>
                    Payments
                </h2>
                {details?.credit_payments?.map((payment, index) => (
                    <React.Fragment key={index}>
                        <div
                            style={{
                                display: 'flex',
                                width: '100%',
                                justifyContent: 'space-between',
                                alignItems: 'center',
                                borderBottom: '1px solid #eee',
                                paddingTop: 5,
                                paddingBottom: 5
                            }}
                        >
                            <div style={{ display: 'flex', flexDirection: 'column' }}>
                                <div style={{ fontWeight: 500 }}>
                                    {moment(payment?.payment_date).format(CommonDateOnlyFormat)}
                                </div>
                                <div style={{ fontWeight: 400 }}>
                                    {payment?.invoice?.invoice_number
                                        ? (
                                            <span
                                                className={`${hasEveryPermission(['View Estimation Details (Page)']) ? 'text-primary cursor-pointer' : ''}`}
                                                onClick={() => {
                                                    if (hasEveryPermission(['View Estimation Details (Page)'])) {
                                                        handleOpenNewTab('invoices', payment?.invoice?.id);
                                                    }
                                                }}
                                            >
                                                {payment?.invoice?.invoice_number}
                                            </span>
                                        )
                                        : (
                                            <span
                                                className={`${hasEveryPermission(['View Sales Orders Details']) ? 'text-primary cursor-pointer' : ''}`}
                                                onClick={() => {
                                                    if (hasEveryPermission(['View Sales Orders Details'])) {
                                                        handleOpenNewTab('sales_orders', payment?.sales_order?.id);
                                                    }
                                                }}
                                            >
                                                {payment?.sales_order?.sales_order_number}
                                            </span>
                                        )
                                    }
                                </div>
                            </div>
                            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end' }}>
                                <div style={{ fontWeight: 500, color: '#28a745' }}>
                                    {getCurrencySymbol(payment?.currency)} {numberFormatter.format(payment?.amount_paid)}
                                </div>
                                <div style={{ fontWeight: 400 }}>
                                    {payment?.user_created?.first_name}
                                </div>
                            </div>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                {payment?.attachment && (
                                    <div
                                        className="btn btn-primary btn-outlined-main white-var btn-sm cursor-pointer"
                                        onClick={() => {
                                            window.open(payment.attachment, '_blank');
                                        }}
                                    >
                                        <div style={{ margin: 'auto 5px auto 3px' }}>
                                            <FaPaperclip />
                                        </div>
                                    </div>
                                )}
                                <PermissionAllowed permissions={['Delete Payment Transaction']}>
                                    <div
                                        className="btn btn-danger btn-outlined-danger white-var btn-sm cursor-pointer"
                                        onClick={() => {
                                            deletePayment(payment?.id);
                                        }}
                                    >
                                        <div style={{ margin: 'auto 3px' }}>
                                            <DeleteIcon height="14px" width="14px" />
                                        </div>
                                    </div>
                                </PermissionAllowed>
                            </div>
                        </div>
                    </React.Fragment>
                ))}
                {showDeletePayment && (
                    <DeleteConfirmation
                        key="paymentDelete"
                        show={showDeletePayment}
                        handleClose={handleClose}
                        handleDelete={() => { }}
                        title="Confirm"
                        message={`Are you sure you want to delete this payment?`}
                        customDeleteButtonText={'Yes, Delete this payment'}
                    />
                )}
            </div>
        );
    }

    // --- DESKTOP VERSION (unchanged) ---
    return (
        <div style={{ padding: '0px 20px 20px 16px' }}>
            <div className="row mb10">
                <div className="col-12">
                    <h2 className="d-flex font600" style={{ fontSize: '18px' }}>Payments</h2>
                </div>
            </div>
            <div className="row">
                <div className="col-md-3 font600">Date</div>
                <div className="col-md-3 font600">Invoice</div>
                <div className="col-md-3 font600">Amount</div>
                <div className="col-md-3 font600">Attachment</div>
            </div>
            <hr className="mt8 mb8" />
            {details?.credit_payments?.map((payment, index) => (
                <React.Fragment key={index}>
                    <div className="row hover-row">
                        <div className="col-md-3">{moment(payment?.payment_date).format(CommonDateOnlyFormat)}</div>
                        <div className="col-md-3">
                            {payment?.invoice?.id ? (
                                <div
                                    className={`${hasEveryPermission(['View Estimation Details (Page)']) ? 'text-primary cursor-pointer' : ''}`}
                                    onClick={() => {
                                        if (hasEveryPermission(['View Estimation Details (Page)'])) {
                                            handleOpenNewTab('invoices', payment?.invoice?.id);
                                        }
                                    }}
                                >
                                    {payment?.invoice?.invoice_number}
                                </div>
                            ) : (
                                <div
                                    className={`${hasEveryPermission(['View Sales Orders Details']) ? 'text-primary cursor-pointer' : ''}`}
                                    onClick={() => {
                                        if (hasEveryPermission(['View Sales Orders Details'])) {
                                            handleOpenNewTab('sales_orders', payment?.sales_order?.id);
                                        }
                                    }}
                                >
                                    {payment?.sales_order?.sales_order_number}
                                </div>
                            )}
                        </div>
                        <div className="col-md-3 text-success">
                            {getCurrencySymbol(payment?.currency)} {numberFormatter.format(payment?.amount_paid)}
                        </div>
                        <div className="col-md-3">{payment?.user_created?.first_name}</div>
                        <div className="hover-button-container-lg mr-20 mt0">
                            <div>
                                <div className="hover-button">
                                    {payment?.attachment && (
                                        <div
                                            className="btn btn-primary btn-outlined-main white-var btn-sm cursor-pointer"
                                            style={{ marginLeft: '8px' }}
                                            onClick={() => {
                                                window.open(payment.attachment, '_blank');
                                            }}
                                        >
                                            <div style={{ margin: 'auto 5px auto 3px' }}>
                                                <FaPaperclip />
                                            </div>
                                        </div>
                                    )}
                                    <PermissionAllowed permissions={['Delete Payment Transaction']}>
                                        
                                        <div
                                            className="btn btn-danger white-var btn-sm cursor-pointer"
                                            style={{ marginLeft: '8px' }}
                                            onClick={() => {
                                                deletePayment(payment?.id);
                                            }}
                                        >
                                            <div style={{ margin: 'auto 3px' }}>
                                                <DeleteIcon height="14px" width="14px" />
                                            </div>
                                        </div>
                                    </PermissionAllowed>

                                    
                                </div>
                            </div>
                        </div>
                    </div>
                    <hr className="mt8 mb8" />
                </React.Fragment>
            ))}
            {showDeletePayment && (
                <DeleteConfirmation
                    key="paymentDelete"
                    show={showDeletePayment}
                    handleClose={handleClose}
                    handleDelete={() => { }}
                    title="Confirm"
                    message={`Are you sure you want to delete this payment?`}
                    customDeleteButtonText={'Yes, Delete this payment'}
                />
            )}
        </div>
    );
};

export default CreditPaymentComponent;
